import {
  calculateOverallDataCompletenessRate,
  calculateOverallPerformanceRate,
  computeDataCompletenessRate,
  computePerformanceRate,
} from './mips-scoring';
import type { StrataData } from './mips-data-service';
import { measureOutcomesCache } from './measure-outcomes-cache';
import userStorage from './userStorage';

interface Counts {
  ip: number;
  ex: number;
  den?: number;
  met: number;
  exc: number;
  not: number;
}

interface SummaryRow {
  measureId: string;
  submissionMethod: string;
  isMultiStrata: boolean;
  counts?: Counts;
  strata?: (Counts & { name: string; description?: string })[];
  overallAlgorithm?: string;
  calculationMode?: string;
  manualValues?: any;
}

// Provider/NPI interface for evaluation type dropdown
export interface Provider {
  npi: string;
  name: string | null;
  displayName: string;
}

// Get API URL for database calls
const getApiUrl = () => {
  return import.meta.env.VITE_API_URL || 'http://localhost:3001';
};

// Get auth token for API calls
const getAuthToken = () => {
  return localStorage.getItem('auth_token');
};

// Get current practice ID from sessionStorage (fallback for non-component contexts)
const getCurrentPracticeId = () => {
  // D12: an explicit ?practiceId= in the URL (APP Plus "View Details" pointing
  // the Detail page at the ACO's shadow practice) WINS over the MIPS session
  // selection — otherwise these library fetches would silently query the
  // wrong practice. Components should still pass practiceId directly when possible.
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('practiceId');
    if (fromUrl) {
      console.log('[getCurrentPracticeId] URL override:', fromUrl);
      return fromUrl;
    }
  } catch { /* non-browser context */ }
  const practiceId = userStorage.getSessionItem('selectedPracticeId');
  console.log('[getCurrentPracticeId] returning', practiceId);
  return practiceId;
};

// Cache for summary data to avoid repeated API calls
let summaryCache: Map<string, SummaryRow[]> | null = null;
let cacheExpiry: number = 0;
const CACHE_DURATION = 300000; // 5 minutes (increased for better performance)

// Evaluation context for NPI-level filtering
interface EvaluationContext {
  evaluationType?: 'GROUP' | 'NPI';
  npi?: string;
  year?: number;
}

// Batch function to get all summaries at once
export async function getAllOutcomeSummaries(
  signal?: AbortSignal,
  evaluationContext?: EvaluationContext
): Promise<SummaryRow[]> {
  const practiceId = getCurrentPracticeId();
  if (!practiceId) {
    console.warn('No practice selected, cannot fetch outcome summaries');
    return [];
  }

  const evaluationType = evaluationContext?.evaluationType || 'GROUP';
  const npi = evaluationContext?.npi;
  const year = evaluationContext?.year;
  const cacheKey = `${practiceId}:${evaluationType}:${npi || 'all'}:${year || 'sys'}`;

  try {
    // Check cache first
    const now = Date.now();
    if (summaryCache && now < cacheExpiry) {
      const cached = summaryCache.get(cacheKey);
      if (cached) {
        console.log('[getAllOutcomeSummaries] Returning cached data for', cacheKey, 'with', cached.length, 'summaries');
        return cached;
      }
    }

    // Fetch from API
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    // Build query params
    const params = new URLSearchParams({ evaluationType });
    if (npi) {
      params.append('npi', npi);
    }
    if (year) {
      params.append('year', String(year));
    }
    
    console.log('[getAllOutcomeSummaries] Fetching fresh data from API for', cacheKey);
    const response = await fetch(`${apiUrl}/api/performance/${practiceId}/summaries?${params}`, {
      signal,
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`API response not ok: ${response.status}`);
    }

    const summaries = await response.json() as SummaryRow[];
    console.log('[getAllOutcomeSummaries] API returned', summaries.length, 'summaries');
    if (summaries.length > 0 && summaries.length <= 10) {
      console.log('[getAllOutcomeSummaries] Summary data:', summaries);
    }
    
    // Update cache
    if (!summaryCache) {
      summaryCache = new Map();
    }
    summaryCache.set(cacheKey, summaries);
    cacheExpiry = now + CACHE_DURATION;
    
    return summaries;

  } catch (error) {
    console.error('Error fetching outcome summaries:', error);
    return [];
  }
}

export async function getOutcomeSummary(
  measureId: string, 
  submissionMethod?: string, 
  signal?: AbortSignal,
  evaluationContext?: EvaluationContext
): Promise<SummaryRow | undefined> {
  if (!submissionMethod) return undefined;
  
  // Use the batch function and find the specific summary
  const allSummaries = await getAllOutcomeSummaries(signal, evaluationContext);
  return allSummaries.find(s => s.measureId === measureId && s.submissionMethod === submissionMethod);
}

export async function getPatientRows(
  measureId: string, 
  submissionMethod: string,
  evaluationContext?: EvaluationContext
) {
  const practiceId = getCurrentPracticeId();
  if (!practiceId) {
    console.warn('No practice selected, cannot fetch patient rows');
    return [];
  }

  const evaluationType = evaluationContext?.evaluationType || 'GROUP';
  const npi = evaluationContext?.npi;
  const year = evaluationContext?.year;
  const cacheKey = `${measureId}:${submissionMethod}:${evaluationType}:${npi || 'all'}:${year || 'sys'}`;

  // Check cache first (with evaluation context in key)
  const cachedData = measureOutcomesCache.get(practiceId, cacheKey, '');
  if (cachedData !== null) {
    return cachedData;
  }

  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    // Build query params
    const params = new URLSearchParams({ 
      submissionMethod,
      evaluationType 
    });
    if (npi) {
      params.append('npi', npi);
    }
    if (year) {
      params.append('year', String(year));
    }
    
    const response = await fetch(
      `${apiUrl}/api/performance/${practiceId}/measures/${measureId}/patients?${params}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`API response not ok: ${response.status}`);
    }

    const data = await response.json();
    
    // Store in cache for future use (with evaluation context in key)
    measureOutcomesCache.set(practiceId, cacheKey, '', data);
    
    return data;

  } catch (error) {
    console.error('Error fetching patient rows:', error);
    return [];
  }
}

// Cache for providers list
let providersCache: Map<string, Provider[]> | null = null;
let providersCacheExpiry = 0;
const PROVIDERS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Get list of providers (NPIs) for a practice
export async function getProviders(): Promise<Provider[]> {
  const practiceId = getCurrentPracticeId();
  if (!practiceId) {
    console.warn('No practice selected, cannot fetch providers');
    return [];
  }

  try {
    // Check cache first
    const now = Date.now();
    if (providersCache && now < providersCacheExpiry) {
      const cached = providersCache.get(practiceId);
      if (cached) {
        console.log('[getProviders] Returning cached data for practice', practiceId, 'with', cached.length, 'providers');
        return cached;
      }
    }

    // Fetch from API
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    console.log('[getProviders] Fetching providers from API for practice', practiceId);
    const response = await fetch(`${apiUrl}/api/performance/${practiceId}/providers`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`API response not ok: ${response.status}`);
    }

    const providers = await response.json() as Provider[];
    console.log('[getProviders] API returned', providers.length, 'providers');
    
    // Update cache
    if (!providersCache) {
      providersCache = new Map();
    }
    providersCache.set(practiceId, providers);
    providersCacheExpiry = now + PROVIDERS_CACHE_DURATION;
    
    return providers;

  } catch (error) {
    console.error('Error fetching providers:', error);
    return [];
  }
}

// Enhanced function to update patient outcomes with conflict resolution
export async function updatePatientOutcome(
  practiceId: string, 
  patientId: string, 
  measureId: string, 
  submissionMethod: string, 
  outcomeChanges: any,
  stratum?: string,  // Add stratum support
  year?: number      // Target performance year (viewing year)
) {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const response = await fetch(
      `${apiUrl}/api/performance/${practiceId}/patients/${patientId}/outcomes${year != null ? `?year=${year}` : ''}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          measureId,
          submissionMethod,
          outcomeChanges,
          stratum  // Include stratum if provided
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to update patient outcome: ${response.status}`);
    }

    const result = await response.json();
    
    // CRITICAL: Clear cache to ensure Summary page gets fresh data
    clearOutcomeCache();
    
    // Also invalidate the measure-specific cache
    const currentPracticeId = getCurrentPracticeId();
    if (currentPracticeId) {
      measureOutcomesCache.invalidate(currentPracticeId, measureId, submissionMethod);
    }
    
    // Return enhanced result with conflict resolution information
    return {
      ...result,
      patientId,
      measureId,
      hasConflictResolution: !!result.conflictResolution
    };

  } catch (error) {
    console.error('Error updating patient outcome:', error);
    throw error;
  }
}

// Batch update multiple patient outcomes with job tracking
export async function batchUpdatePatientOutcomes(
  practiceId: string,
  updates: Array<{
    patientId: string;
    measureId: string;
    submissionMethod: string;
    evaluationType?: string;
    outcomeChanges: any;
    encounterId?: string;
    stratum?: string;  // Add stratum support
  }>,
  year?: number      // Target performance year (viewing year)
) {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const response = await fetch(
      `${apiUrl}/api/performance/${practiceId}/patients/batch${year != null ? `?year=${year}` : ''}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ updates }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to batch update patient outcomes: ${response.status}`);
    }

    const result = await response.json();
    
    // Store job info for tracking
    if (result.jobId) {
      const jobInfo = {
        jobId: result.jobId,
        status: result.status,
        updateCount: result.updateCount,
        timestamp: Date.now()
      };
      sessionStorage.setItem('currentBatchJob', JSON.stringify(jobInfo));
    }
    
    return result;
  } catch (error) {
    console.error('Error in batch update:', error);
    throw error;
  }
}

// Check job status
export async function checkJobStatus(jobId: string) {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const response = await fetch(
      `${apiUrl}/api/performance/jobs/${jobId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null; // Job not found
      }
      throw new Error(`Failed to check job status: ${response.status}`);
    }

    const job = await response.json();
    
    // If job is complete, clear caches
    if (job.status === 'completed') {
      clearOutcomeCache();
      measureOutcomesCache.clear();
    }
    
    return job;
  } catch (error) {
    console.error('Error checking job status:', error);
    return null;
  }
}

// Clear cache when practice changes or after edits
export function clearOutcomeCache() {
  summaryCache = null;
  cacheExpiry = 0;
  
  // Also clear the individual measure cache
  measureOutcomesCache.clear();
  
  // Clear localStorage overrides for all measures
  const keys = Object.keys(localStorage);
  keys.forEach(key => {
    if (key.startsWith('measureOverrides:')) {
      localStorage.removeItem(key);
    }
  });
  
  // Force clear session storage as well
  sessionStorage.removeItem('currentBatchJob');
  sessionStorage.removeItem('detailUpdatePending');
  
  console.log('[Cache] Cleared all outcome caches and overrides');
}

/* Helpers to convert summary -> MeasureRow used by Gaps page */
export function toMeasureRow(summary: SummaryRow, title?: string) {
  if (!summary.isMultiStrata) {
    const c = summary.counts!;
    const den = c.den ?? c.ip - c.ex;
    const pr = computePerformanceRate(c.met, c.not, c.exc);
    const dc = computeDataCompletenessRate(den, c.met, c.not, c.exc);

    return {
      id: summary.measureId,
      title: title || summary.measureId,
      submissionMethod: summary.submissionMethod,
      isMultiStrata: false,
      ip: c.ip,
      ex: c.ex,
      den,
      met: c.met,
      exc: c.exc,
      not: c.not,
      pr,
      dc,
      calculationMode: summary.calculationMode,
      manualValues: summary.manualValues,
    };
  }

  /* multi-strata */
  const strata: StrataData[] = summary.strata!.map((s) => {
    const den = s.den ?? s.ip - s.ex;
    const pr = computePerformanceRate(s.met, s.not, s.exc);
    const dc = computeDataCompletenessRate(den, s.met, s.not, s.exc);
    return {
      ...s,
      description: s.description || '',
      den,
      pr,
      dc,
    };
  });

  const overallPr = calculateOverallPerformanceRate(strata, summary.overallAlgorithm ?? 'Simple Average');
  const overallDc = calculateOverallDataCompletenessRate(strata, 'Weighted Average');

  return {
    id: summary.measureId,
    title: title || summary.measureId,
    submissionMethod: summary.submissionMethod,
    isMultiStrata: true,
    strata,
    overallAlgorithm: summary.overallAlgorithm,
    overallPr,
    overallDc,
    pr: overallPr, // for backward compatibility and simple sorting
    dc: overallDc,
    calculationMode: summary.calculationMode,
    manualValues: summary.manualValues,
  };
}